import { useCallback, useEffect, useMemo, useRef } from 'react'

import { navigate } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_EMPLOYEE_LEADERBOARD } from 'src/graphql/pipelineMetrics.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import AnalyticsCard from 'src/pages/AnalyticsPage/components/AnalyticsCard'
import {
  LeaderboardCardView,
  type LeaderboardColumn,
} from 'src/pages/AnalyticsPage/components/LeaderboardCardView'

import { buildApplicantsFilterUrl } from '../buildApplicantsFilterUrl'

import { buildEmployeeDisplayName } from './employeeDisplayName'
import type { PerformanceFilters } from './PerformanceTab'

const TITLE = 'Employee Performance'

const INFO_TEXT =
  'Per-employee metrics for the selected date range. An employee appears if they have any leads, hires, or active applicants in the period. Cohort Conv is the true within-period conversion: of applicants who applied in this period, the fraction eventually hired. Recent windows may trend low while cohorts mature. Time-to-hire averages days from application to hire. 90d retention is null when the employee has no hires in the period.'

const COHORT_CONV_TOOLTIP =
  'Cohort conversion: of applicants who applied in this period, the fraction eventually hired. Recent windows may trend low while cohorts are still maturing.'

// ---------------------------------------------------------------------------
// Local types — match the SDL `EmployeePerformanceLeaderboardRow` shape.
// Defined locally so this file doesn't depend on a recent codegen run.
// ---------------------------------------------------------------------------
interface EmployeeLeaderboardServerRow {
  employeeId: string
  employee: {
    employeeId: string
    firstName: string | null
    lastName: string | null
  }
  leads: number
  hires: number
  conversionRate: number | null
  avgDaysToHire: number | null
  retention90d: number | null
  activeCount: number
  cohortHires: number
  cohortConversionRate: number | null
}

interface EmployeeLeaderboardServerResponse {
  employeePerformanceLeaderboard: ReadonlyArray<EmployeeLeaderboardServerRow>
}

/** Display row shape consumed by the generic LeaderboardCardView. */
interface EmployeeLeaderboardRow {
  id: string
  name: string
  leads: number
  hires: number
  conversionRate: number | null
  avgDaysToHire: number | null
  retention90d: number | null
  activeCount: number
  cohortHires: number
  cohortConversionRate: number | null
}

const formatPct = (v: number | null): string =>
  v == null ? '—' : `${v.toFixed(1)}%`
const formatDays = (v: number | null): string =>
  v == null ? '—' : `${v.toFixed(1)}d`

/**
 * Build the column config with click handlers bound to the current
 * dateRange. The clickable cells navigate to /applicants filtered on the
 * same cohort the displayed number was computed from — see the SQL in
 * `api/src/lib/pipelineAnalytics/fetchEmployeeLeaderboard.ts` for the
 * authoritative semantics each cell mirrors. Rate/average cells
 * (Conv %, Cohort Conv %, Time to hire, 90d retention) deliberately have no
 * onCellClick: landing on an Applicants list "for a percentage" has no
 * coherent cohort. Cohort Hires is also intentionally non-clickable for now
 * — drilling into a cohort needs filtering HIRED applicants by
 * applicationDate (not hiredDate), which buildApplicantsFilterUrl doesn't
 * natively support; left as a follow-up rather than ship a half-correct
 * click target.
 */
const buildColumns = (
  filters: PerformanceFilters
): ReadonlyArray<LeaderboardColumn<EmployeeLeaderboardRow>> => {
  // The resolver honors the page-level facets, so the deep-links carry them
  // too — otherwise the Applicants list would open on a broader cohort than
  // the number that was clicked. Per-row links pin recruiterIds to the row's
  // employee (a subset of any page-level recruiter selection by
  // construction); the totals row forwards the page selection as-is.
  const { dateRange } = filters
  const navigateForRow = (
    row: EmployeeLeaderboardRow,
    options: Pick<
      Parameters<typeof buildApplicantsFilterUrl>[0],
      'mode' | 'stage'
    >
  ) =>
    navigate(
      buildApplicantsFilterUrl({
        dateRange,
        recruiterIds: [row.id],
        jobListingIds: filters.jobListings,
        leadSources: filters.linkTypes,
        ...options,
      })
    )
  const navigateForTotals = (
    options: Pick<
      Parameters<typeof buildApplicantsFilterUrl>[0],
      'mode' | 'stage'
    >
  ) =>
    navigate(
      buildApplicantsFilterUrl({
        dateRange,
        recruiterIds: filters.recruiters,
        jobListingIds: filters.jobListings,
        leadSources: filters.linkTypes,
        ...options,
      })
    )

  // Cohort columns sit right after Conv so the activity/cohort pair reads as
  // a unit; Time to Hire / retention / Active stay to the right.
  return [
    {
      key: 'name',
      label: 'Employee',
      align: 'left',
      sortable: false,
      // Clicking the employee name lands on all their applicants in the
      // window (no stage filter) — useful for "open everything this
      // employee touched this period."
      onCellClick: (row) => navigateForRow(row, {}),
    },
    {
      key: 'leads',
      label: 'Leads',
      // Mirrors the SQL filter `a.application_date BETWEEN gte AND lte`.
      onCellClick: (row) => navigateForRow(row, {}),
      onTotalsCellClick: () => navigateForTotals({}),
    },
    {
      key: 'hires',
      label: 'Hires',
      // Mirrors `a.hired_date BETWEEN gte AND lte` — stage-event mode with
      // stage=HIRED picks hiredDate as the date column.
      onCellClick: (row) =>
        navigateForRow(row, { stage: ApplicantStage.HIRED }),
      onTotalsCellClick: () =>
        navigateForTotals({ stage: ApplicantStage.HIRED }),
    },
    // Activity Conv column intentionally removed (was confusing alongside
    // Cohort Conv when the two diverged for the same row
    // history). Cohort Conv is the single source of truth.
    { key: 'cohortHires', label: 'Cohort Hires' },
    {
      key: 'cohortConversionRate',
      label: 'Cohort Conv',
      render: (r) => formatPct(r.cohortConversionRate),
      tooltip: COHORT_CONV_TOOLTIP,
    },
    {
      key: 'avgDaysToHire',
      label: 'Time to hire',
      render: (r) => formatDays(r.avgDaysToHire),
    },
    {
      key: 'retention90d',
      label: '90d retention',
      render: (r) => formatPct(r.retention90d),
    },
    {
      key: 'activeCount',
      label: 'Active',
      // Mirrors `a.applicantStage = 'PROSPECT' AND a.application_date in
      // range` — that's current-stage semantics, not a stage event.
      onCellClick: (row) =>
        navigateForRow(row, {
          mode: 'current-stage',
          stage: ApplicantStage.PROSPECT,
        }),
      onTotalsCellClick: () =>
        navigateForTotals({
          mode: 'current-stage',
          stage: ApplicantStage.PROSPECT,
        }),
    },
  ]
}

export interface EmployeeLeaderboardContainerProps {
  /** Page-level FiltersBar state — the resolver honors every facet. */
  filters: PerformanceFilters
  /** Bumped by the page-level Refresh button to trigger a manual refetch. */
  refreshKey: number
}

const EmployeeLeaderboardContainer = ({
  filters,
  refreshKey,
}: EmployeeLeaderboardContainerProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } =
    useQuery<EmployeeLeaderboardServerResponse>(GET_EMPLOYEE_LEADERBOARD, {
      variables: { input: { filters } },
      onError,
      notifyOnNetworkStatusChange: true,
    })

  // Skip the initial mount — useQuery already kicked off the first fetch.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const rows: ReadonlyArray<EmployeeLeaderboardRow> = useMemo(() => {
    const serverRows = data?.employeePerformanceLeaderboard ?? []
    return serverRows.map((r) => ({
      id: r.employeeId,
      name: buildEmployeeDisplayName(
        r.employee.firstName,
        r.employee.lastName,
        r.employeeId
      ),
      leads: r.leads,
      hires: r.hires,
      conversionRate: r.conversionRate,
      avgDaysToHire: r.avgDaysToHire,
      retention90d: r.retention90d,
      activeCount: r.activeCount,
      cohortHires: r.cohortHires,
      cohortConversionRate: r.cohortConversionRate,
    }))
  }, [data])

  // Overall totals row. Conv % is hires-over-leads at the company level (NOT
  // an average of per-employee rates — that would double-weight low-volume
  // employees). Time-to-hire and retention are hires-weighted so big-volume
  // recruiters dominate the overall mean. Mirrors SourceBreakdownCard.
  const totals = useMemo<
    Partial<Record<keyof EmployeeLeaderboardRow & string, React.ReactNode>>
  >(() => {
    if (rows.length === 0) return {}
    const totalLeads = rows.reduce((s, r) => s + r.leads, 0)
    const totalHires = rows.reduce((s, r) => s + r.hires, 0)
    const totalActive = rows.reduce((s, r) => s + r.activeCount, 0)
    const totalCohortHires = rows.reduce((s, r) => s + r.cohortHires, 0)

    const overallCohortConv =
      totalLeads > 0 ? (totalCohortHires / totalLeads) * 100 : null

    // Each employee's avgDaysToHire is itself an average of their hires —
    // weight by hires count to recover the underlying total day-count, then
    // divide by total hires for the company-wide mean.
    const weightedDaysSum = rows.reduce(
      (s, r) =>
        r.avgDaysToHire != null && r.hires > 0
          ? s + r.avgDaysToHire * r.hires
          : s,
      0
    )
    const overallDays = totalHires > 0 ? weightedDaysSum / totalHires : null

    // Same hires-weighted logic for retention. Employees with zero hires
    // have no retention contribution — they're skipped.
    const weightedRetentionSum = rows.reduce(
      (s, r) =>
        r.retention90d != null && r.hires > 0
          ? s + r.retention90d * r.hires
          : s,
      0
    )
    const overallRetention =
      totalHires > 0 ? weightedRetentionSum / totalHires : null

    return {
      leads: totalLeads.toLocaleString(),
      hires: totalHires.toLocaleString(),
      cohortHires: totalCohortHires.toLocaleString(),
      cohortConversionRate: formatPct(overallCohortConv),
      avgDaysToHire: formatDays(overallDays),
      retention90d: formatPct(overallRetention),
      activeCount: totalActive.toLocaleString(),
    }
  }, [rows])

  // Rebuild columns whenever the date range moves so the click handlers
  // navigate to the current window — not a stale one captured at first render.
  const columns = useMemo(() => buildColumns(filters), [filters])

  return (
    <AnalyticsCard title={TITLE} info={INFO_TEXT}>
      <LeaderboardCardView<EmployeeLeaderboardRow>
        columns={columns}
        rows={rows}
        loading={loading}
        defaultSortKey="hires"
        defaultSortDir="desc"
        emptyMessage="No applicants in this period."
        totals={totals}
      />
    </AnalyticsCard>
  )
}

export default EmployeeLeaderboardContainer
